import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { dateUtils } from "/imports/libs/url";
import { unavailableReasonProps } from "/imports/libs/unavailableReason";
import { DataView, EmptyState, PickerRulesPanel, ServiceAvailabilityNotice } from "/imports/ui/components/shared";
import {
  TimeSlotCard,
  SLOT_SECTION_LABEL_SX,
  getUniformGridPrice,
  SUGGESTED_BUCKET_ID,
  PackageOfferBanner,
  PackComposerTray,
  PackHorizonBanner,
  PackContinuationBanner
} from "/imports/ui/components/booking";
import { pickerContinuationCopy } from "/imports/libs/packContinuation";
import DurationPrompt from "./components/DurationPrompt";
import LessonMobileFilterBar from "./components/LessonMobileFilterBar";
import LessonFilterSheet from "./components/LessonFilterSheet";
import LessonRescheduleBanner from "./components/LessonRescheduleBanner";
import LessonRescheduleDialog from "./components/LessonRescheduleDialog";
import LessonRescheduleTray from "./components/LessonRescheduleTray";
import { rescheduleEmptyProps, packWindowEmptyProps } from "./lessonHelpers";

// Mobile layout — sticky filter chip bar + sticky date strip + segmented slot
// list. Filter and multi-option pickers live in bottom sheets.
function LessonsMobileLayout({ lesson, scrollSpy }) {
  const {
    navigate,
    slug,
    currency,
    filters,
    setFilter,
    setSelectedSlot,
    loading,
    error,
    availability,
    bucketedSlots,
    suggestedGroups,
    packageOffer,
    handleViewPackage
  } = lesson;
  const { sectionRefs, sectionOffset } = scrollSpy;
  // A dated closure / time off emptied the day: say so, and offer the lever
  // the client actually has (drop the resource filter) rather than the date.
  const closureProps = unavailableReasonProps({
    closureReasons: availability?.closureReasons,
    canClearResource: !!filters.trainerId && lesson.allowTrainerPick !== false,
    onClearResource: () => {
      setFilter("trainerId", null);
      setSelectedSlot(null);
    },
    onNextDate: () => {
      setFilter("date", dateUtils.toUrlDate(new Date(Date.now() + 86400000)));
      setSelectedSlot(null);
    }
  });

  // One shared price across the whole day → drop it from every cell (the
  // duration picker already states it); varying prices stay per-cell.
  const uniformDayPrice = getUniformGridPrice(bucketedSlots.flatMap((b) => b.groups));

  const packMode = lesson.pack?.active;
  const continuationCopy = packMode ? pickerContinuationCopy(lesson.packContinuation, lesson.pack.opt?.linkedCategoryType) : null;
  const isRescheduling = lesson.reschedule?.active;
  // Moving a lesson: a day outside the package's dates is explained before anything else.
  const packWindowEmpty = isRescheduling ? packWindowEmptyProps(lesson.reschedule.info, { from: filters.date, onJumpTo: lesson.jumpToDate }) : null;
  // The tray of the client's own booked lessons sits at the bottom, like the pack tray.
  const hasOwnTray = isRescheduling && (lesson.reschedule.info?.ownBookings?.length || 0) > 0;

  const renderSlotCard = (group) => {
    const isMulti = group.slots.length > 1;
    return (
      <TimeSlotCard
        key={group.time}
        time={group.time}
        price={group.minPrice}
        currency={group.currency || currency}
        selected={packMode && lesson.pack.isPicked(filters.date, group.time)}
        onClick={() => lesson.handleGroupClick(group)}
        optionsLabel={lesson.getOptionsLabel(group.slots)}
        pricePrefix={isMulti ? "from" : null}
        hidePrice={packMode || uniformDayPrice != null}
        dense
      />
    );
  };

  return (
    <Box sx={{ pb: packMode ? { xs: "11rem", md: "7rem" } : hasOwnTray ? { xs: "7rem", md: "7rem" } : 0 }}>
      {/* Compact hero */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
        <IconButton size="small" onClick={lesson.goBack} aria-label="Go back" sx={{ ml: -0.5 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={{ fontSize: "1.4rem", fontWeight: 800, letterSpacing: "-0.02em" }}>
          {packMode ? "Pick your pack sessions" : isRescheduling ? "Move your lesson" : "Book a Private Lesson"}
        </Typography>
      </Box>

      {isRescheduling && <LessonRescheduleBanner reschedule={lesson.reschedule} currency={currency} sx={{ mb: 1.5 }} />}

      {/* The club's rules for buying a pack, in the one place the client can still
          act on them. Only while composing a pack — an ordinary booking has no
          pack rules to obey. */}
      {packMode && <PickerRulesPanel title="Before you buy" rules={lesson.pack.packRules} />}

      {packMode && continuationCopy && <PackContinuationBanner {...continuationCopy} onAction={lesson.goToPackContinuation} sx={{ mb: 2 }} />}

      <LessonMobileFilterBar lesson={lesson} scrollSpy={scrollSpy} />

      {/* Slots — all buckets stacked in one scroll, each section anchored so
          tapping a segment (or scrolling) keeps the nav in sync. */}
      <Box mt={2}>
        {/* Below the filters, with the days it is talking about — the same place
            the classes page keeps it on a phone. Never while BUYING a pack:
            those sessions belong to a different one. */}
        {!packMode && !isRescheduling && (
          <PackHorizonBanner
            horizon={lesson.ownedPackHorizon}
            start={lesson.ownedPackStart}
            count={lesson.ownedPackCount}
            capped={lesson.ownedPackCapped}
            noun="lessons"
            onJump={lesson.goToPackStart}
            sx={{ mb: 2 }}
          />
        )}

        {!filters.duration ? (
          <DurationPrompt />
        ) : (
          <DataView loading={loading} error={error} data={availability} loadingMessage="Loading availability...">
            {bucketedSlots.length > 0 ? (
              <Box>
                {/* Scrolls away with the results rather than eating into the
                    sticky filter bar above it. */}
                <PackageOfferBanner offer={packageOffer} currency={currency} onClick={handleViewPackage} sx={{ mb: 2.5 }} />
                {suggestedGroups.length > 0 && (
                  <Box
                    data-bucket={SUGGESTED_BUCKET_ID}
                    ref={(el) => {
                      sectionRefs.current[SUGGESTED_BUCKET_ID] = el;
                    }}
                    sx={{ scrollMarginTop: `${sectionOffset}px`, mb: 3.5 }}
                  >
                    <Typography sx={{ ...SLOT_SECTION_LABEL_SX, mb: 1.5 }}>Suggested for you · {suggestedGroups.length}</Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.25 }}>{suggestedGroups.map(renderSlotCard)}</Box>
                  </Box>
                )}
                {bucketedSlots.map((bucket) => (
                  <Box
                    key={bucket.id}
                    data-bucket={bucket.id}
                    ref={(el) => {
                      sectionRefs.current[bucket.id] = el;
                    }}
                    sx={{ scrollMarginTop: `${sectionOffset}px`, mb: 3.5 }}
                  >
                    <Typography sx={{ ...SLOT_SECTION_LABEL_SX, mb: 1.5 }}>
                      {bucket.label} · {bucket.groups.length}
                    </Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.25 }}>{bucket.groups.map(renderSlotCard)}</Box>
                  </Box>
                ))}
              </Box>
            ) : packWindowEmpty ? (
              <EmptyState {...packWindowEmpty} />
            ) : closureProps ? (
              <EmptyState {...closureProps} />
            ) : availability?.availabilityInfo?.length > 0 ? (
              <ServiceAvailabilityNotice
                availabilityInfo={availability.availabilityInfo}
                actionLabel="Try Tomorrow"
                onAction={() => {
                  setFilter("date", dateUtils.toUrlDate(new Date(Date.now() + 86400000)));
                  setSelectedSlot(null);
                }}
              />
            ) : isRescheduling ? (
              <EmptyState
                {...rescheduleEmptyProps(lesson.reschedule.info, {
                  onNext: () => {
                    setFilter("date", dateUtils.toUrlDate(new Date(Date.now() + 86400000)));
                    setSelectedSlot(null);
                  }
                })}
              />
            ) : (
              <EmptyState
                title="No availability"
                description="No trainers are available for the selected date and filters. Try a different date or adjust your filters."
                actionLabel="Try Tomorrow"
                onAction={() => {
                  setFilter("date", dateUtils.toUrlDate(new Date(Date.now() + 86400000)));
                  setSelectedSlot(null);
                }}
              />
            )}
          </DataView>
        )}
      </Box>

      <LessonFilterSheet lesson={lesson} />
      <PackComposerTray pack={lesson.pack} onGoToDate={lesson.jumpToDate} />
      {isRescheduling && <LessonRescheduleTray reschedule={lesson.reschedule} isMobile />}
      {isRescheduling && <LessonRescheduleDialog reschedule={lesson.reschedule} currency={currency} isMobile />}
    </Box>
  );
}

export default LessonsMobileLayout;
